import DN from "owp.dn";
import type { ComponentChildren, JSX } from "preact";
import { Fragment } from "preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { Auth } from "@gss/common/Auth";
import { Roles } from "@gss/common/Roles";
import { sweCompare } from "@gss/common/sweCompare";
import { Alert } from "@gss/components/Alert";
import { Badge } from "@gss/components/Badge";
import { Button } from "@gss/components/Button";
import { Col } from "@gss/components/Col";
import { FormLabel } from "@gss/components/FormLabel";
import { useMessages } from "@gss/components/Messages";
import "./FreeTextTab.css";
import { Pagination } from "@gss/components/Pagination";
import { Row } from "@gss/components/Row";
import { Select } from "@gss/components/Select";
import type { MeasurementStat } from "@gss/types/measurementStat";
import { DAO } from "./DAO";
import { ExportRawDisclaimer } from "./download/ExportRawDisclaimer";
import { isAllOption, SVF_FILTER_VALUE } from "./filters/FilterUtil";
import type { FreeTextResponseDTO } from "./types";
import { round1 } from "./util/formatters";
import { useTranslation } from "./util/Translation";

interface Organisation {
    id: string;
    name: string;
}

export function FreeTexTab({ stat }: { stat: MeasurementStat }): JSX.Element {
    const [organisations, setOrganisations] = useState<Organisation[]>([]);
    const [organisationNames, setOrganisationNames] = useState<
        Record<string, string>
    >({});
    const [responses, setResponses] = useState<FreeTextResponseDTO[]>();
    const [selectedQuestionId, setSelectedQuestionId] = useState<string>();
    const [selectedOrganisation, setSelectedOrganisation] = useState<string>();
    const [selectedSvf, setSelectedSvf] = useState<string>();
    const [count, setCount] = useState(-1);
    const [limit, setLimit] = useState(25);
    const [currentPage, setCurrentPage] = useState(0);
    const { translation } = useTranslation();
    const { addError } = useMessages();
    const t = translation.freeText;
    const tP = translation.pagination;

    const questions = useMemo(() => {
        const questions = stat.questions.filter(
            // oxlint-disable-next-line typescript/prefer-nullish-coalescing
            (q) => q.choiceFreeText || q.choices?.some((c) => c.freeText),
        );
        // Sort so that questions with choiceFreeText come first
        questions.sort((a, b) => {
            if (a.choiceFreeText && !b.choiceFreeText) {
                return -1;
            }
            if (!a.choiceFreeText && b.choiceFreeText) {
                return 1;
            }
            return 0;
        });
        return questions;
    }, [stat.questions]);

    const svfFilter = useMemo(
        () => stat.filters.find((obj) => obj.value === SVF_FILTER_VALUE),
        [stat.filters],
    );

    const svfNames = useMemo(() => {
        const svfNames: Record<string, string> = {};
        for (const o of svfFilter?.options ?? []) {
            svfNames[o.value] = o.name;
        }
        return svfNames;
    }, [svfFilter]);

    useEffect(() => {
        setSelectedQuestionId(questions[0]?.id);
    }, [questions]);

    useEffect(() => {
        DAO.freeText
            .getOrganisations(stat.measurement, stat.periods)
            .then((organisations) => {
                const orgs: Organisation[] = [];
                const orgNames: Record<string, string> = {};
                for (const o of organisations) {
                    const name = new DN(o.dn).getLastValue();
                    orgs.push({
                        id: o.id,
                        name,
                    });
                    orgNames[o.id] = name;
                }
                orgs.sort((a, b) => sweCompare(a.name, b.name));
                setOrganisations(orgs);
                setOrganisationNames(orgNames);
            })
            .catch(addError);
    }, [stat.measurement, stat.periods]);

    useEffect(() => {
        if (selectedQuestionId == null) {
            return;
        }
        setResponses(undefined);
        Promise.all([
            DAO.freeText.getCount(
                stat.measurement,
                stat.periods,
                selectedQuestionId,
                selectedOrganisation,
                selectedSvf,
            ),
            DAO.freeText.getAnswers(
                stat.measurement,
                stat.periods,
                selectedQuestionId,
                selectedOrganisation,
                selectedSvf,
                limit,
                0,
            ),
        ])
            .then((res) => {
                setCount(res[0].count);
                setCurrentPage(0);
                setResponses(res[1]);
            })
            .catch(addError);
    }, [selectedQuestionId, selectedOrganisation, selectedSvf]);

    useEffect(() => {
        if (selectedQuestionId == null) {
            return;
        }
        setResponses([]);
        const offset = currentPage * limit;
        DAO.freeText
            .getAnswers(
                stat.measurement,
                stat.periods,
                selectedQuestionId,
                selectedOrganisation,
                selectedSvf,
                limit,
                offset,
            )
            .then(setResponses)
            .catch(addError);
    }, [currentPage, limit]);

    const getChildText = (dimension: string) => {
        return dimension === "Öppet barn" ? ` (${t.child})` : null;
    };

    const renderQuestions = () => {
        return (
            <Select onChange={setSelectedQuestionId} value={selectedQuestionId}>
                {questions.map((question) => ({
                    value: question.id,
                    children: (
                        <>
                            {question.id}
                            {getChildText(question.dimension)}
                        </>
                    ),
                }))}
            </Select>
        );
    };

    const renderSvf = () => {
        return (
            <Select value={selectedSvf} onChange={setSelectedSvf}>
                {svfFilter?.options?.map((o) => ({
                    // API expects undefined for "all"
                    value: isAllOption(o.value) ? undefined : o.value,
                    children: o.name,
                })) ?? []}
            </Select>
        );
    };

    const renderOrganisations = () => {
        return (
            <Select
                className="float-start"
                value={selectedOrganisation}
                onChange={setSelectedOrganisation}
            >
                {[
                    { value: undefined, children: t.noOrganisationSelected },
                    ...organisations.map((org) => ({
                        value: org.id,
                        children: org.name,
                    })),
                ]}
            </Select>
        );
    };

    const renderFilters = () => {
        return (
            <div>
                <div className="py-2">
                    <h3>{t.filters}</h3>
                    <Row>
                        <Col>
                            <FormLabel>{t.question}</FormLabel>
                            {renderQuestions()}
                        </Col>
                        {svfFilter != null && (
                            <Col>
                                <FormLabel>{t.svf}</FormLabel>
                                {renderSvf()}
                            </Col>
                        )}
                        <Col>
                            <FormLabel>{t.organisation}</FormLabel>
                            {renderOrganisations()}
                        </Col>
                    </Row>
                </div>
            </div>
        );
    };

    const renderDownloadButton = () => {
        if (
            selectedQuestionId == null ||
            !Auth.hasRole(Roles.EXPORT_FREE_TEXT)
        ) {
            return null;
        }
        return (
            <>
                <hr />
                <ExportRawDisclaimer name="fritextsvar" />
                <Button
                    variant="primary"
                    onClick={() =>
                        void DAO.freeText.downloadExport(
                            stat.measurement,
                            stat.periods,
                            selectedQuestionId,
                            selectedOrganisation,
                            selectedSvf,
                        )
                    }
                >
                    {t.downloadFreeTextExport}
                </Button>
            </>
        );
    };

    const renderRes = () => {
        let res;
        if (responses == null) {
            res = null;
        } else if (count === 0) {
            res = <Alert variant="warning">{t.cantFindAnswers}</Alert>;
        } else {
            res = renderResponses();
        }
        return (
            <div>
                <Row>
                    <Col>
                        <h3>
                            {t.questionAnswers}
                            <small>{renderBadge()}</small>
                        </h3>
                        {renderOffsetText()}
                    </Col>

                    <Col xs="2">
                        <FormLabel>{t.numPerPage}</FormLabel>
                        <Select
                            value={String(limit)}
                            onChange={(value) => setLimit(Number(value))}
                        >
                            {limitOptions.map((limit) => ({
                                value: String(limit),
                                children: limit,
                            }))}
                        </Select>
                    </Col>
                </Row>

                {responses ? renderPagination(true) : null}
                <section aria-label={t.paginatedFreeTextAnswers}>{res}</section>
                {responses && responses.length > 0 ? (
                    <>
                        {renderPagination(false)}
                        {renderDownloadButton()}
                    </>
                ) : null}
            </div>
        );
    };

    const renderResponses = () => {
        if (responses == null) {
            return null;
        }
        return (
            <table className="free-text-table">
                <tbody>
                    {responses.map((response, i) => (
                        <Fragment key={i}>
                            {selectedOrganisation != null && (
                                <tr>
                                    <td colSpan={2} className="free-text-name">
                                        <b>
                                            {organisationNames[response.orgId]}
                                        </b>
                                    </td>
                                </tr>
                            )}
                            <tr>
                                <td className="free-text-meta">
                                    {renderMetadata(response)}
                                </td>
                                <td>{response.questionText}</td>
                            </tr>
                            <tr>
                                <td colSpan={2}>&nbsp;</td>
                            </tr>
                        </Fragment>
                    ))}
                </tbody>
            </table>
        );
    };

    const renderBadge = () => {
        if (responses == null) {
            return null;
        }
        return <Badge className="ms-1">{count}</Badge>;
    };

    const renderOffsetText = () => {
        if (responses == null) {
            return null;
        }
        const min = currentPage * limit;
        const max = Math.min(min + limit, count);
        return (
            <p className="help-block">
                {t.showingAnswers}
                &nbsp;
                {min + 1}-{max}
            </p>
        );
    };

    const renderMetadata = (response: FreeTextResponseDTO) => {
        const metaFields = [
            "svf",
            "periodMonth",
            "periodQuarter",
            "overallScore",
            "categories",
        ] as const satisfies readonly (keyof FreeTextResponseDTO)[];

        return metaFields.map((field) => {
            let children: ComponentChildren;

            switch (field) {
                case "svf": {
                    if (response.svf == null) {
                        return null;
                    }
                    children = (
                        <span title={svfNames[response.svf]}>
                            {response.svf}
                        </span>
                    );
                    break;
                }
                case "periodMonth":
                case "periodQuarter": {
                    const value = response[field];
                    if (value == null) {
                        return null;
                    }
                    children = value;
                    break;
                }
                case "overallScore": {
                    if (response.overallScore == null) {
                        return null;
                    }
                    children = round1(response.overallScore);
                    break;
                }
                case "categories": {
                    if (response.categories == null) {
                        return null;
                    }
                    children = formatList(response.categories);
                    break;
                }
                // no default
            }
            return (
                <Fragment key={field}>
                    {t[field]}: {children}
                    <br />
                </Fragment>
            );
        });
    };

    const renderPagination = (isTop: boolean) => {
        return (
            <Pagination
                className="justify-content-center"
                current={currentPage}
                size={count}
                limit={limit}
                onChange={setCurrentPage}
                ariaLabel={isTop ? tP.paginationTop : tP.paginationBottom}
                firstLabel={tP.first}
                lastLabel={tP.last}
                previousLabel={tP.previous}
                nextLabel={tP.next}
            />
        );
    };

    return (
        <div>
            <section role="search" aria-label={t.filterFreeTextAnswers}>
                {renderFilters()}
            </section>
            <section aria-label={t.resultsFreeTextAnswers}>
                {renderRes()}
            </section>
        </div>
    );
}

const limitOptions = [10, 25, 50, 100, 250];

function formatList(list: string[]) {
    if (list.length > 1) {
        return (
            <>
                <br />
                <ul>
                    {list.map((v) => (
                        <li key={v}>{v}</li>
                    ))}
                </ul>
            </>
        );
    }
    return list[0];
}
